import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";

import type { VerificationSessionSubject } from "../types/verification-api.types";

export interface ConfirmSummaryProps {
  subject: VerificationSessionSubject;
  submitting?: boolean;
  onConfirm: () => void;
}

interface SummaryRowProps {
  label: string;
  value: string;
  mono?: boolean;
}

function SummaryRow({ label, value, mono }: SummaryRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-sm text-foreground" : "text-sm font-medium text-foreground"}>
        {value}
      </dd>
    </div>
  );
}

/**
 * Read-only confirmable summary for the data / company step in the public verification link, where the
 * identity already exists — the data is shown from `session.subject`, not editable. The editable form
 * (reusing `Person/BusinessHolderForm`) belongs to the authenticated create flow.
 */
export function ConfirmSummary({ subject, submitting, onConfirm }: ConfirmSummaryProps) {
  const { t } = useTranslations("verification");

  return (
    <div className="space-y-6">
      <div className="space-y-1 text-center">
        <h1 className="text-2xl font-semibold text-foreground">{t("confirm_title")}</h1>
        <p className="text-sm text-muted-foreground">{t("data_subtitle")}</p>
      </div>

      <dl className="divide-y divide-border rounded-xl border border-border">
        <SummaryRow label={t("confirm_field_name")} value={subject.display_name} />
        <SummaryRow label={t("confirm_field_country")} value={subject.country} mono />
        <SummaryRow label={t("confirm_field_document_type")} value={subject.identification_type} mono />
      </dl>

      <ColocaButton variant="primary" className="w-full" loading={submitting} onClick={onConfirm}>
        {t("confirm_cta_confirm")}
      </ColocaButton>
    </div>
  );
}
